/**
 * ShipmentTable (ShipmetTable.jsx)
 * ---------------------------------
 * جدول الشحنات الرئيسي مع التوسعة الداخلية لبيانات الكونتينرات والمركبات.
 * الهدف من هذا الترتيب: تسهيل الصيانة، إضافة تعليقات توضيحية، والحفاظ على جميع الخصائص الحالية بدون تغيير السلوك.
 * (All functional behavior preserved – only structural comments & light organization added.)
 */

import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { keyframes } from '@mui/system';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Menu,
 

  
} from "@mui/material";
import {
  ModeEdit as ModeEditIcon,
  DeleteForever as DeleteForeverIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LastPage as LastPageIcon,
  FirstPage as FirstPageIcon,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  LocalShipping,
 
  Person,
  Group as GroupIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  WorkOutline as WorkOutlineIcon,
  MoreVert as MoreVertIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  MarkEmailRead as MarkEmailReadIcon,
  EmailOutlined as EmailOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { TreeView, TreeItem } from '@mui/lab';
import { visuallyHidden } from "@mui/utils";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ShipmentPrintPreview from './ShipmentPrintPreview';
import ContainerPrintPreview from './ContainerPrintPreview';
import { exportShipmentDetailPDF } from '../../../../util/exportUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { resetState } from "../../../../slices/vehicleInfoSlice";
import SendEmailsDialog from './SendEmailsDialog';
// Job creation logic moved into CreateJobDialog component
// Removed inline Job Dialog related imports (moved to CreateJobDialog component)
import CreateJobDialog from './CreateJobDialog';
import CollapseView from './CollapseView';
import EditJobStartDateDialog from './EditJobStartDateDialog';
import MissingInfoDialog from './MissingInfoDialog';
import UploadBillOfLadingDialog from './UploadBillOfLadingDialog';
import ConfirmShippingDocsDialog from './ConfirmShippingDocsDialog';
import ConfirmDepartureDialog from './ConfirmDepartureDialog';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import YardIcon from '@mui/icons-material/Yard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import Progress from "../../../../components/Loader/Progress ";

// =============================================================================
// Table Head Cell Definitions (الاعمدة الرئيسية للجدول)
// =============================================================================
const headCells = [
  { id: "expand", label: "", disablePadding: false },
  { id: "s.id", label: "ID", disablePadding: false },
  { id: "s.entryDate", label: "Entry Date", disablePadding: false },
  { id: "number", label: "Booking Number", disablePadding: false },
  { id: "shipmentMode", label: "Shipment Mode", disablePadding: false },
  { id: "bookingStatus", label: "Status", disablePadding: false },
  { id: "job.jobStatus", label: "Job Status", disablePadding: false },
  { id: "primaryClient", label: "Clients", disablePadding: false },
  { id: "booking.vesselName", label: "Vessel Name", disablePadding: false },
  { id: "booking.voyageNumber", label: "Voyage Number", disablePadding: false },
  { id: "booking.destination", label: "Destination", disablePadding: false },
  { id: "booking.shippingLine", label: "Shipping Line", disablePadding: false },
  { id: "booking.broker", label: "Broker", disablePadding: false },
  { id: "booking.portLoading", label: "Port Loading", disablePadding: false },
  { id: "booking.pickupTerminal", label: "Pickup Terminal", disablePadding: false },
  { id: "booking.returnTerminal", label: "Return Terminal", disablePadding: false },
  { id: "booking.earliestReceivingDate", label: "Earliest Receiving Date", disablePadding: false },
  { id: "booking.latestReturnDate", label: "Latest Return Date", disablePadding: false },
  { id: "booking.cutoffDate", label: "DOC Cutoff Date", disablePadding: false },
  { id: "booking.estimatedSailingDate", label: "Estimated Sailing Date", disablePadding: false },
  { id: "booking.estimatedArrivalDate", label: "Estimated Arrival Date", disablePadding: false },
  { id: "actions", label: "Actions", disablePadding: false },
];


const statusColor = (s) => {
  switch (s) {
    case 'Finished':
      return 'success';
    case 'Loading Vehicles':
      return 'info';
    case 'Preparing Vehicles':
      return 'warning';
    case 'Departed':
      return 'error';
    default:
      return 'error';
  }
};

const renderShipmentClients = (row) => {
  const secondaryClients = Array.isArray(row?.shipmentClients)
    ? row.shipmentClients.filter((client) => client?.role?.toString().toUpperCase() === 'SECONDARY')
    : [];

  return (
    <Box sx={{ minWidth: 220 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 240,
        }}
      >
        {row?.primaryClientName || '-'}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{
          mt: 0.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 240,
        }}
      >
        {row?.primaryClientCompany ? `"${row.primaryClientCompany}"` : '-'}
      </Typography>
      {secondaryClients.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, alignItems: 'center' }}>
          {secondaryClients.map((client) => (
            <Chip
              key={`${client?.shipmentId || row?.id}-${client?.id || client?.name || client?.company}`}
              size="small"
              label={`${client?.name || client?.company || 'N/A'}`}
              variant="outlined"
              sx={{
                borderColor: 'secondary.main',
                color: 'secondary.main',
                backgroundColor: 'transparent',
                fontSize: 11,
                height: 24,
                textTransform: 'capitalize',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// EnhancedTableHead: رأس الجدول مع دعم الفرز بالضغط على الأعمدة
// =============================================================================
function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          // Hide actions column when selecting a shipment

          return (
            <TableCell
              key={headCell.id}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
              variant="head"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {headCell.id !== "expand" && headCell.id !== "actions" ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {t(headCell.label).toUpperCase()}
                  {orderBy === headCell.id && (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </Box>
                  )}
                </TableSortLabel>
              ) : (
                t(headCell.label)
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

// =============================================================================
// Custom Pagination Actions (أزرار التنقل بين الصفحات)
// =============================================================================
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}




const ShipmentTable = ({ handlingFetchData }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchData = useSelector((state) => state.manageV2.getManage);
  const userReducer = useSelector((state) => state.user);
  const currencySymbol = useSelector((state) => state?.settings?.settingsMap?.currencySymbol
    || "$");


  // ---------------- UI / Permission State ----------------
  const [isAbleDelete, setAbleDelete] = useState(false);              // امتلاك صلاحية الحذف
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);  // نافذة تأكيد الحذف
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null); // العنصر المستهدف للحذف
  const [openArray, setOpenArray] = useState({ Name: [] });           // الصفوف المفتوحة (Collapse)
  const [dense, setDense] = useState(true);                           // كثافة الجدول (Padding)
  const [printRow, setPrintRow] = useState(null);                     // الشحنة المختارة للطباعة
  const [printOpen, setPrintOpen] = useState(false);                  // فتح نافذة الطباعة الرئيسية
  const [containerPrint, setContainerPrint] = useState({ open: false, row: null, index: null }); // طباعة كونتينر محدد
  // const [exporting, setExporting] = useState(false);                  // (تم تعطيله بعد إزالة أدوات التصدير)
  const [jobDialogRow, setJobDialogRow] = useState(null);             // الشحنة المختارة لإنشاء Job
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, row: null }); // قائمة المزيد لكل صف
  const [editJobDialog, setEditJobDialog] = useState({ open: false, row: null });
  const [missingInfoDialog, setMissingInfoDialog] = useState({ open: false, row: null }); // حوار البيانات الناقصة
  const [billDialog, setBillDialog] = useState({ open: false, row: null }); // حوار رفع Bill of Lading
  const [docsDialog, setDocsDialog] = useState({ open: false, row: null }); // حوار ارسال الاوراق
  const [departDialog, setDepartDialog] = useState({ open: false, row: null }); // حوار تاكيد الترحيل
  // Tree expansion state for the action menu (keeps categories open/closed)
  const [expandedPdfNodes, setExpandedPdfNodes] = useState(['standard', 'consignee']);
  const [sendDialog, setSendDialog] = useState({ open: false, row: null, taskType: null });
  // حفظ الـ IDs التي تم تشغيل الأنيميشن لها مرة واحدة (حتى لا تتكرر)
  const [animatedJobIds, setAnimatedJobIds] = useState(new Set());
  const [animatedMissingIds, setAnimatedMissingIds] = useState(new Set());
  const [animatedBillIds, setAnimatedBillIds] = useState(new Set());

  const data = fetchData.object || {};
  const content = data.content || [];
  const hasMissingInfo = (row) => (row?.containers || []).some(c => !c?.containerNumber || !c?.sealNumber);
  const pulseWarn = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, .6); }
    70% { box-shadow: 0 0 0 6px rgba(255, 193, 7, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
  `;
  const hasElectricOrHybridVehicle = (row) => {
    const electricFuelTypes = ['electric', 'hybrid'];

    return (row?.containers || []).some((container) =>
      (container?.shipmentItems || []).some((item) => {
        const fuelType = item?.vehicle?.fuelType?.name || item?.vehicle?.fuelType;
        return electricFuelTypes.includes(fuelType?.toString().trim().toLowerCase());
      })
    );
  };
const hasMissingBillOfLadingFile = (row) => {
  const shipmentMode = row?.shipmentMode || row?.type;
  const isMissingFile = (file) => file == null || (Array.isArray(file) && file.length === 0);

  if (shipmentMode === 'LCL') {
    return (row?.containers || []).some((container) =>
      (container?.shipmentItems || []).some((item) => isMissingFile(item?.billOfLadingFile))
    );
  }

  const file = row?.primaryBillOfLadingFile;
  return isMissingFile(file);
};
  const displayContent = content; // لم يعد هناك فلتر حالة

  // URL query parameters and their setters
  const page = Number(searchParams.get("PageNumber")) || 0;
  const rowsPerPage = Number(searchParams.get("PageSize")) || 5;
  const order = searchParams.get("SortOrder") || "desc";
  const orderBy = searchParams.get("SortBy") || "id";

  // ---------------- URL Query Updaters (مساعدات لتحديث معايير البحث في العنوان) ----------------
  const setPage = (value) => {
    searchParams.set("PageNumber", value);
    setSearchParams(searchParams);
  };
  const setRowsPerPage = (value) => {
    searchParams.set("PageSize", value);
    setSearchParams(searchParams);
  };
  const setOrder = (value) => {
    searchParams.set("SortOrder", value);
    setSearchParams(searchParams);
  };
  const setOrderBy = (value) => {
    searchParams.set("SortBy", value);
    setSearchParams(searchParams);
  };

  // ---------------- Sorting (الفرز) ----------------
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleChangeDense = (event) => setDense(event.target.checked);

  const handelEdit = (id) => {
    dispatch(resetState());
    navigate(`/addShipment?id=${id}&backLink=` + searchParams.toString().replaceAll("&", "*"));
  };

  const handelOpenDeleteModal = (item) => {
    setSelectedDeleteItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  // ---------------- Printing / Preview ----------------
  const handleOpenPrint = (row) => {
    setPrintRow(row);
    setPrintOpen(true);
  };
  const handleClosePrint = () => {
    setPrintOpen(false);
    setPrintRow(null);
  };

  const handleOpenContainerPrint = (containerIndex, shipment) => {
    const targetShipment = shipment || null;
    setContainerPrint({ open: true, row: targetShipment, index: containerIndex });
  };
  const handleCloseContainerPrint = () => {
    setContainerPrint({ open: false, row: null, index: null });
  };

  const handleViewContainer = (row) => {
    const bookingId = row?.booking?.id;
    if (bookingId) {
      navigate(`/containers?BookingId=${bookingId}`);
    }
  };

  const handleViewVehicles = (row) => {
    const bookingId = row?.booking?.id;
    if (bookingId) {
      navigate(`/vehicles?BookingId=${bookingId}`);
    }
  };

  // Export handlers
  // ---------------- Export Handlers ----------------
  const handleExportDetailPDF = (row, preview = false, withConsigneeDock ) => {
    exportShipmentDetailPDF(row, 'shipment', { mode: preview ? 'preview' : 'download' },  currencySymbol ,withConsigneeDock );
  };

  const openJobDialog = (row) => setJobDialogRow(row);
  const closeJobDialog = () => setJobDialogRow(null);
  const openMissingInfoDialog = (row) => setMissingInfoDialog({ open: true, row });
  const closeMissingInfoDialog = () => setMissingInfoDialog({ open: false, row: null });
  const handleSaveMissingInfo = () => {
    handlingFetchData();
    closeMissingInfoDialog();
  };
  const closeBillDialog = () => setBillDialog({ open: false, row: null });
  const openDocsDialog = (row) => setDocsDialog({ open: true, row });
  const closeDocsDialog = () => setDocsDialog({ open: false, row: null });
  const handleSaveDocs = () => {
    handlingFetchData();
    closeDocsDialog();
  };
  const openDepartDialog = (row) => setDepartDialog({ open: true, row });
  const closeDepartDialog = () => setDepartDialog({ open: false, row: null });
  const handleSaveDepart = () => {
    handlingFetchData();
    closeDepartDialog();
  };

  const openActionMenu = (e, row) => {
    e.stopPropagation();
    setActionMenu({ anchorEl: e.currentTarget, row });
  };
  const closeActionMenu = () => setActionMenu({ anchorEl: null, row: null });

  // Toggle handler for the TreeView (category expand/collapse)
  const handleTogglePdfNode = (event, nodeIds) => {
    setExpandedPdfNodes(nodeIds);
  };

  // Send email context derived from currently-open action menu row
  const sendRow = actionMenu.row || null;
   const _client =  sendRow?.primaryClientId ? { id: sendRow.primaryClientId , name: sendRow.primaryClientName  , company: sendRow.primaryClientCompany ,email: sendRow.primaryClientEmail } : {};
  const _shippingLine = sendRow?.booking?.shippingLine || {};
  const _broker = sendRow?.broker || sendRow?.booking?.broker || {};
  const hasClientEmail = Boolean(_client?.email);
  const hasShippingEmail = Boolean(_shippingLine?.email);
  const hasBrokerEmail = Boolean(_broker?.email);
  const shippingOptionAvailable = hasShippingEmail || hasBrokerEmail;
  const sendBlockedDueMissingInfo = Boolean(sendRow && hasMissingInfo(sendRow));

  // ---------------- Row Collapse Toggle ----------------
  const openCollapse = (index) => {
    const lastArray = [...openArray.Name];
    const idx = lastArray.indexOf(index);
    if (idx > -1) {
      lastArray.splice(idx, 1);
    } else {
      lastArray.push(index);
    }
    setOpenArray({ Name: lastArray });
  };
  const checkIOpen = (index) => openArray.Name.indexOf(index) > -1;

  // ---------------- Permission Detection (صلاحيات الحذف) ----------------
  useEffect(() => {
    userReducer?.userInfo?.user?.roles.some((role) => {
      if (
        role.name === "ROLE_SUPER_EDITOR" ||
        role.name === "ROLE_SUPER" ||
        role.name === "ROLE_ADMIN"
      ) {
        setAbleDelete(true);
        return true;
      }
      return false;
    });
  }, [userReducer]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.totalElements) : 0;

  if (
    fetchData.loading === "pending" ||
    fetchData.loading === "idle" ||
    userReducer.loading === true
  ) {
    return <Progress Msg="Loading..." />;
  }

  return (
    <Box sx={{ width: "100%", bgcolor: '#F7F8FA', p: { xs: 0, md: 2 }, borderRadius: 3 }}>
      <Paper sx={{ width: "100%", mb: 2, borderRadius: 3, boxShadow: '0 2px 12px 0 rgba(11, 60, 93, 0.10)' }}>
        <TableContainer sx={{ borderRadius: 3 }}>
          <Table
            sx={{ minWidth: 750, background: '#fff', borderRadius: 3 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {displayContent.map((row, index) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    sx={{
                      backgroundColor: hasMissingInfo(row) ? '#FFF5F5' : (!row.jobStatus ? '#FFFBEA' : (index % 2 === 0 ? '#F7F8FA' : '#fff')),
                      '&:hover': { backgroundColor: '#E9ECEF' },
                      transition: 'background 0.2s',
                    }}
                    tabIndex={-1}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>

                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => openCollapse(index)}
                        >
                          {checkIOpen(index) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {hasElectricOrHybridVehicle(row) && (
                          <Tooltip title={t('Hybrid/Electric vehicles included')} arrow>
                            <Chip
                              size="small"
                              label={t('Hybrid/Electric')}
                              color="error"
                              variant="outlined"
                              sx={{
                                animation: `${pulseWarn} 1.6s ease-out infinite`,
                              }}
                              icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
                            />
                          </Tooltip>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row?.id}</Typography>

                          {hasMissingInfo(row) && (
                            <Tooltip title={t('Missing information')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => openMissingInfoDialog(row)}
                                aria-label={t('Missing information')}
                                onAnimationEnd={() => setAnimatedMissingIds(prev => { if (prev.has(row.id)) return prev; const n = new Set(prev); n.add(row.id); return n; })}
                                sx={() => {
                                  const firstTime = !animatedMissingIds.has(row.id);
                                  return {
                                    width: 30, height: 30, p: 0.5,
                                    bgcolor: '#FFEBEE',
                                    border: '1.5px solid #E53935',
                                    position: 'relative',
                                    animation: firstTime ? 'wiggle 0.9s ease-in-out 0s 1' : '',
                                    '@keyframes wiggle': {
                                      '0%,100%': { transform: 'rotate(0deg)' },
                                      '20%': { transform: 'rotate(-10deg)' },
                                      '40%': { transform: 'rotate(8deg)' },
                                      '60%': { transform: 'rotate(-6deg)' },
                                      '80%': { transform: 'rotate(4deg)' }
                                    },
                                    '&:hover': { bgcolor: '#FFCDD2', animation: 'wiggle 0.9s ease-in-out 0s 1' }
                                  };
                                }}
                              >
                                <WarningAmberRoundedIcon sx={{ fontSize: 17, color: '#C62828' }} />
                              </IconButton>
                            </Tooltip>
                          )}

                        

                        {!hasMissingInfo(row) && !row?.booking?.shippingInstructionsSent && (
                          <Tooltip title={t('Confirm shipping documents sent')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => openDocsDialog(row)}
                              aria-label={t('Confirm shipping documents sent')}
                              sx={{ width: 30, height: 30, p: 0.5, bgcolor: '#E8F5E9', border: '1.5px solid #43A047', '&:hover': { bgcolor: '#C8E6C9' } }}
                            >
                              <MarkEmailReadIcon sx={{ fontSize: 17, color: '#2E7D32' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!hasMissingInfo(row) && !!row?.booking?.shippingInstructionsSent && !row?.booking?.shipmentDepartedDate && row?.jobStatus === "Finished" && (
                          <Tooltip title={t('Confirm shipment departed')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => openDepartDialog(row)}
                              aria-label={t('Confirm shipment departed')}
                              sx={{ width: 30, height: 30, p: 0.5, bgcolor: '#FFF8E1', border: '1.5px solid #FFB300', '&:hover': { bgcolor: '#FFE082' } }}
                            >
                              <FlightTakeoffIcon sx={{ fontSize: 17, color: '#E65100' }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {hasMissingBillOfLadingFile(row) && (
                          <Tooltip title={t('Missing Bill of Lading')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => setBillDialog({ open: true, row })}
                              aria-label={t('Missing Bill of Lading')}
                              onAnimationEnd={() => setAnimatedBillIds(prev => { if (prev.has(row.id)) return prev; const n = new Set(prev); n.add(row.id); return n; })}
                              sx={() => {
                                const firstTime = !animatedBillIds.has(row.id);
                                return {
                                  width: 30,
                                  height: 30,
                                  p: 0.5,
                                  bgcolor: '#FFF8E1',
                                  border: '1.5px solid #FFB300',
                                  position: 'relative',
                                  animation: firstTime ? 'wiggleBill 0.9s ease-in-out 0s 1' : '',
                                  '@keyframes wiggleBill': {
                                    '0%,100%': { transform: 'rotate(0deg)' },
                                    '20%': { transform: 'rotate(-8deg)' },
                                    '40%': { transform: 'rotate(6deg)' },
                                    '60%': { transform: 'rotate(-4deg)' },
                                    '80%': { transform: 'rotate(2deg)' }
                                  },
                                  '&:hover': { bgcolor: '#FFF3E0', animation: 'wiggleBill 0.9s ease-in-out 0s 1' }
                                };
                              }}
                            >
                              <PictureAsPdfIcon sx={{ fontSize: 17, color: '#FF8F00' }} />
                            </IconButton>
                          </Tooltip>
                        )}

                      </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{row?.entryDate}</TableCell>
                    <TableCell>{row?.booking?.number}</TableCell>
                    <TableCell>{row?.shipmentMode}</TableCell>
                    <TableCell>{row?.booking?.bookingStatus?.name}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                        {!row.jobStatus ? (
                          <Tooltip title={t('Create Job')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => openJobDialog(row)}
                              aria-label={t('Create Job')}
                              onAnimationEnd={() => setAnimatedJobIds(prev => { if (prev.has(row.id)) return prev; const n = new Set(prev); n.add(row.id); return n; })}
                              sx={() => {
                                const firstTime = !animatedJobIds.has(row.id);
                                return {
                                  width: 30, height: 30, p: 0.5,
                                  bgcolor: '#FFF8E1',
                                  border: '1.5px solid #FFB300',
                                  boxShadow: firstTime ? '0 0 0 0 rgba(255,179,0,0.4)' : 'none',
                                  animation: firstTime ? 'pulseJob 1.3s ease-out 0s 1' : '',
                                  '@keyframes pulseJob': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(255,179,0,0.6)' },
                                    '70%': { boxShadow: '0 0 0 10px rgba(255,179,0,0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(255,179,0,0)' }
                                  },
                                  '&:hover': { bgcolor: '#FFE082', animation: 'pulseJob 1.1s ease-out 0s 1' }
                                };
                              }}
                            >
                              <WorkOutlineIcon sx={{ fontSize: 16, color: '#E65100' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          (() => {
                            const editable = ['Loading Vehicles', 'Preparing Vehicles'].includes(row?.job?.jobStatus);
                            const chip = (
                              <Chip
                                size="small"
                                label={row?.jobStatus || 'N/A'}
                                color={statusColor(row?.jobStatus)}
                                sx={{ fontWeight: 500, '& .MuiChip-deleteIcon': { color: 'inherit' }, '& .MuiChip-deleteIcon:hover': { color: 'inherit' } }}
                                {...(editable ? { onDelete: () => setEditJobDialog({ open: true, row }), deleteIcon: <EditCalendarIcon sx={{ fontSize: 16 }} /> } : {})}
                              />
                            );
                            return editable ? (
                              <Tooltip title={t('Edit Start Date')} arrow>{chip}</Tooltip>
                            ) : chip;
                          })()
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>{renderShipmentClients(row)}</TableCell>

                    <TableCell>{row?.booking?.vesselName}</TableCell>
                    <TableCell>{row?.booking?.voyageNumber}</TableCell>
                    <TableCell>{row?.booking?.destination?.name}</TableCell>
                    <TableCell>{row?.booking?.shippingLine?.name}</TableCell>
                    <TableCell>{row?.booking?.broker?.name ? row?.booking?.broker?.name : 'N/A'}</TableCell>
                    <TableCell>{row?.booking?.portLoading?.name}</TableCell>
                    <TableCell>{row?.booking?.pickupTerminal?.name}</TableCell>
                    <TableCell>{row?.booking?.returnTerminal?.name}</TableCell>
                    <TableCell>{row.booking?.earliestReceivingDate}</TableCell>
                    <TableCell>{row?.booking?.latestReturnDate}</TableCell>
                    <TableCell>{row?.booking?.cutoffDate}</TableCell>
                    <TableCell>{row?.booking?.estimatedSailingDate}</TableCell>
                    <TableCell>{row?.booking?.estimatedArrivalDate}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Create Job icon نقل إلى عمود الـ ID كأيقونة متحركة */}
                        <Tooltip title={t('Print / Preview')} arrow>
                          <IconButton size="small" onClick={() => handleOpenPrint(row)} sx={{ bgcolor: '#E6F2FE', color: '#0D47A1', width: 28, height: 28 }}>
                            <PrintIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Edit')} arrow>
                          <IconButton size="small" onClick={() => handelEdit(row.id)} sx={{ bgcolor: '#F1E4FA', color: '#6A1B9A', width: 28, height: 28 }}>
                            <ModeEditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('More')} arrow>
                          <IconButton size="small" onClick={(e) => openActionMenu(e, row)} sx={{ bgcolor: '#F5F7F9', color: '#455A64', width: 28, height: 28 }}>
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length}>
                      <Collapse in={checkIOpen(index)} timeout="auto" unmountOnExit>
                        <CollapseView row={row} onOpenContainerPrint={handleOpenContainerPrint} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={headCells.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          ActionsComponent={TablePaginationActions}
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: '0 1px 4px 0 rgba(11, 60, 93, 0.08)',
            mt: 1
          }}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label={t("Densepadding")}
        sx={{ mt: 1, ml: 1 }}
      />
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={()=>{handleCloseDeleteModal(); handlingFetchData();}}
          deleteItem={selectedDeleteItem}
        />
      )}
      <ShipmentPrintPreview open={printOpen} onClose={handleClosePrint} row={printRow} />
      <ContainerPrintPreview open={!!containerPrint.open} onClose={handleCloseContainerPrint} row={containerPrint.row} containerIndex={containerPrint.index} />
      {!!jobDialogRow && <CreateJobDialog open={!!jobDialogRow} row={jobDialogRow} onClose={closeJobDialog} searchParams={searchParams} />}
      <MissingInfoDialog open={missingInfoDialog.open} onClose={closeMissingInfoDialog} row={missingInfoDialog.row} onSave={handleSaveMissingInfo} />
      {/* Bill of Lading upload dialog (extracted to reduce inline code) */}
      <UploadBillOfLadingDialog
        open={billDialog.open}
        onClose={closeBillDialog}
        row={billDialog.row}
        onSave={() => { handlingFetchData(); closeBillDialog(); }}
        handlingFetchData={handlingFetchData}
      />
      <ConfirmShippingDocsDialog open={docsDialog.open} onClose={closeDocsDialog} row={docsDialog.row} onSave={handleSaveDocs} />
      <ConfirmDepartureDialog open={departDialog.open} onClose={closeDepartDialog} row={departDialog.row} onSave={handleSaveDepart} />
      {!!editJobDialog.open && <EditJobStartDateDialog open={editJobDialog.open} row={editJobDialog.row} onClose={() => setEditJobDialog({ open: false, row: null })} searchParams={searchParams} />}
      {/* Main Action Menu as Tree Structure */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 280, maxHeight: 500 }
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <TreeView
            expanded={expandedPdfNodes}
            onNodeToggle={handleTogglePdfNode}
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            sx={{ 
              overflowY: 'auto',
              '& .MuiTreeItem-root': { py: 0.25 },
              '& .MuiTreeItem-content': { py: 0.25, px: 0.5 }
            }}
          >
 
            {/* Export PDF Category */}
            <TreeItem 
              nodeId="export-pdf" 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdfIcon sx={{ fontSize: 18, color: '#D32F2F' }} />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{t('Export PDF')}</span>
                </Box>
              }
            >
              {/* Standard PDF */}
              <TreeItem 
                nodeId="standard-pdf" 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 }}>
                    <PictureAsPdfIcon sx={{ fontSize: 16, color: '#D32F2F' }} />
                    <span style={{ fontWeight: 500 }}>{t('Standard PDF')}</span>
                  </Box>
                }
              >
                <TreeItem 
                  nodeId="standard-download"
                  label={
                    <Box 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (actionMenu.row) { handleExportDetailPDF(actionMenu.row, false, false); }
                        closeActionMenu();
                      }}
                      sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' }, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 14, color: '#E91E63' }} />
                      <span style={{ fontSize: 12 }}>{t('Download')}</span>
                    </Box>
                  }
                />
                <TreeItem 
                  nodeId="standard-preview"
                  label={
                    <Box 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (actionMenu.row) { handleExportDetailPDF(actionMenu.row, true, false); }
                        closeActionMenu();
                      }}
                      sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' }, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <VisibilityIcon sx={{ fontSize: 14, color: '#2196F3' }} />
                      <span style={{ fontSize: 12 }}>{t('Preview')}</span>
                    </Box>
                  }
                />
              </TreeItem>

              {/* With Consignee Dock */}
              <TreeItem 
                nodeId="consignee-pdf" 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 }}>
                    <PictureAsPdfIcon sx={{ fontSize: 16, color: '#F57C00' }} />
                    <span style={{ fontWeight: 500 }}>{t('With Consignee Dock')}</span>
                  </Box>
                }
              >
                <TreeItem 
                  nodeId="consignee-download"
                  label={
                    <Box 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (actionMenu.row) { handleExportDetailPDF(actionMenu.row, false, true); }
                        closeActionMenu();
                      }}
                      sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' }, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 14, color: '#FF6F00' }} />
                      <span style={{ fontSize: 12 }}>{t('Download')}</span>
                    </Box>
                  }
                />
                <TreeItem 
                  nodeId="consignee-preview"
                  label={
                    <Box 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (actionMenu.row) { handleExportDetailPDF(actionMenu.row, true, true); }
                        closeActionMenu();
                      }}
                      sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' }, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <VisibilityIcon sx={{ fontSize: 14, color: '#2196F3' }} />
                      <span style={{ fontSize: 12 }}>{t('Preview')}</span>
                    </Box>
                  }
                />
              </TreeItem>
            </TreeItem>


                       {/* Send Email Category */}
            <TreeItem
              nodeId="send-email"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{t('Send Email')}</span>
                </Box>
              }
            >
              <TreeItem
                nodeId="send-client"
                label={
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!sendBlockedDueMissingInfo && hasClientEmail) {
                        setSendDialog({ open: true, row: actionMenu.row, taskType: 'client' });
                        closeActionMenu();
                      }
                    }}
                    sx={{ cursor: sendBlockedDueMissingInfo || !hasClientEmail ? 'not-allowed' : 'pointer', p: 0.5, borderRadius: 1, display: 'flex', gap: 0.5, alignItems: 'center', color: sendBlockedDueMissingInfo || !hasClientEmail ? 'text.disabled' : 'text.primary' }}
                  >
                    <Person sx={{ fontSize: 14 }} />
                    <span style={{ fontSize: 12 }}>{t('Send to Client')}</span>
                  </Box>
                }
              />

              <TreeItem
                nodeId="send-shipping"
                label={
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!sendBlockedDueMissingInfo && shippingOptionAvailable) {
                        setSendDialog({ open: true, row: actionMenu.row, taskType: 'company' });
                        closeActionMenu();
                      }
                    }}
                    sx={{ cursor: sendBlockedDueMissingInfo || !shippingOptionAvailable ? 'not-allowed' : 'pointer', p: 0.5, borderRadius: 1, display: 'flex', gap: 0.5, alignItems: 'center', color: sendBlockedDueMissingInfo || !shippingOptionAvailable ? 'text.disabled' : 'text.primary' }}
                  >
                    <LocalShipping sx={{ fontSize: 14, color: 'info.main' }} />
                    <span style={{ fontSize: 12 }}>{t('Send to Shipping Line or Broker')}</span>
                  </Box>
                }
              />

              <TreeItem
                nodeId="send-all"
                label={
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!sendBlockedDueMissingInfo && shippingOptionAvailable && hasClientEmail) {
                        setSendDialog({ open: true, row: actionMenu.row, taskType: 'all' });
                        closeActionMenu();
                      }
                    }}
                    sx={{ cursor: sendBlockedDueMissingInfo || !shippingOptionAvailable || !hasClientEmail ? 'not-allowed' : 'pointer', p: 0.5, borderRadius: 1, display: 'flex', gap: 0.5, alignItems: 'center', color: sendBlockedDueMissingInfo || !shippingOptionAvailable || !hasClientEmail ? 'text.disabled' : 'text.primary' }}
                  >
                    <GroupIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                    <span style={{ fontSize: 12 }}>{t('Send to All')}</span>
                  </Box>
                }
              />
            </TreeItem>


            {/* View Container */}
            <TreeItem 
              nodeId="view-container" 
              label={
                <Box 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (actionMenu.row) { handleViewContainer(actionMenu.row); }
                    closeActionMenu();
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' } }}
                >
                  <YardIcon sx={{ fontSize: 18, color: '#1976D2' }} />
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{t('View Container')}</span>
                </Box>
              }
            />

            {/* View Vehicles */}
            <TreeItem 
              nodeId="view-vehicles" 
              label={
                <Box 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (actionMenu.row) { handleViewVehicles(actionMenu.row); }
                    closeActionMenu();
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F5F5F5' } }}
                >
                  <DirectionsCarIcon sx={{ fontSize: 18, color: '#388E3C' }} />
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{t('View Vehicles')}</span>
                </Box>
              }
            />

            {/* Delete */}
            {isAbleDelete && (
              <TreeItem 
                nodeId="delete-item" 
                label={
                  <Box 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (actionMenu.row) { handelOpenDeleteModal(actionMenu.row); }
                      closeActionMenu();
                    }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#FFEBEE' } }}
                  >
                    <DeleteForeverIcon sx={{ fontSize: 18, color: '#D32F2F' }} />
                    <span style={{ fontWeight: 500, fontSize: 13, color: '#D32F2F' }}>{t('Delete')}</span>
                  </Box>
                }
              />
            )}
          </TreeView>
        </Box>
      </Menu>
      <SendEmailsDialog
        open={sendDialog.open}
        onClose={() => setSendDialog({ open: false, row: null, taskType: null })}
        row={sendDialog.row}
        taskType={sendDialog.taskType}
      />
    </Box>
  );
};

ShipmentTable.displayName = "ShipmentTable";
ShipmentTable.propTypes = {
  handlingFetchData: PropTypes.func.isRequired,
  isClient: PropTypes.bool.isRequired,
};

export default ShipmentTable;
